<template>
  <div class="workspace" :class="{ 'with-banner': showBanner }">
    <WorkspaceFirstPanel
      class="column-1"
      ref="firstPanelRef"
      @update:ticket="fetchTicket({ preventLoading: true })"
    />
    <WorkspaceSecondPanel
      class="column-2"
      :key="`${route.query.ticket}-${route.query.interaction}`"
      @update:ticket="fetchTicket({ preventLoading: true })"
    />
    <WorkspaceThirdPanel class="column-3" :key="route.query.client" />
  </div>
</template>

<script setup>
import {
  inject,
  ref,
  provide,
  watch,
  computed,
  reactive,
  onBeforeUnmount,
  onMounted,
  nextTick,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { message } from "ant-design-vue";

import { fetchListTags } from "@/app/workspace/services";
import { ticketService } from "@/app/workspace/services/TicketService";
import { useSocket } from "@/app/shared/composables/useSocket";
import { useJssip } from "@/app/shared/composables/useJssip";
import { SentType } from "@/@types/ivr/sent";

import WorkspaceFirstPanel from "@/app/workspace/components/sections/WorkspaceFirstPanel.vue";
import WorkspaceSecondPanel from "@/app/workspace/components/sections/WorkspaceSecondPanel.vue";
import WorkspaceThirdPanel from "@/app/workspace/components/sections/WorkspaceThirdPanel.vue";

import { registerEventManagement } from "@/app/shared/services";
import { EventManagement } from "@/app/shared/utils/enums";
import { getDataLoggedUser } from "@/app/settings/services";
import EventBus from "@/app/shared/utils/eventBus";
import { WorkspaceEvent } from "@/app/workspace/domain/events";
import { Event } from "@/packages/vue-jssip/jssip/events";

import * as Sentry from "@sentry/vue";
import { ModeFetchWorkspace } from "../../utils/enums";
import { safeRegisterEventManagement } from "@/app/shared/composables/useRegisterEventManagement";

const route = useRoute();
const router = useRouter();
const store = useStore();
const jssip = useJssip();

const showBanner = inject("showBanner");

// Filters
const filterState = reactive({
  search: "",
  channel: "",
  queue: "",
  identifier: "",
  read: "",
  tag: "",
  type_strategy: null,
});
const workspaceDataSourceLocal = reactive({
  tags: [],
});

provide("filterState", filterState);

// Workspace logic
const isTicketLoading = ref(false);
provide("isTicketLoading", isTicketLoading);

const ticketsListLoading = ref(false);
provide("ticketsListLoading", ticketsListLoading);

const ticket = ref(null);
const ticketInteractionsChannels = ref([]);

const isInCall = computed(() => jssip.isInPhoneCall);
const ticketWebhone = computed(() => jssip.phone.meta.ticket);

let currentPromise = null;
let currentTicketId = null;
let currentTicketIdPending = null;
let requestId = 0;

watch([isInCall, ticketWebhone, () => ticket.value?.portfolio], () => {
  if (!isInCall.value) {
    jssip.setMeta("ivr", null);
    return;
  }

  const ticketPortfolio = ticket.value?.portfolio;

  if (ticketPortfolio && jssip.phone.isCurrentCallTicket(route.query.ticket))
    jssip.setMeta("ivr", {
      ...ticketPortfolio,
      shouldSent: ticketPortfolio?.survey_submission_type === SentType.Automatic,
    });
});

const fetchTicket = async ({ preventLoading = false } = {}) => {
  const ticketId = route.query.ticket;

  if (!preventLoading) isTicketLoading.value = true;

  try {
    console.log("fetchTicket", { route, ticketId, param: route.query.ticket });
    if (ticketId) {
      currentTicketIdPending = ticketId;
      const response = await ticketService.getDetails(ticketId);
      ticket.value = { ...response, type_dialing: response?.first_type_dialing };
    } else {
      ticket.value = null;
    }
  } catch (error) {
    console.error(error);
    message.error("Ha ocurrido un error");
  }

  isTicketLoading.value = false;
};

const fetchInteractionsChannels = async () => {
  const ticketId = Number(route.query.ticket);

  if (!ticketId) {
    ticketInteractionsChannels.value = [];
    return;
  }

  // If a request is already running, wait for it
  if (currentPromise && currentTicketId === ticketId) {
      return currentPromise;
  }

  const myRequestId = ++requestId;
  currentTicketId = ticketId;

  currentPromise = (async () => {
    try {
      await store.dispatch("fetchInteractionsChannels", ticketId);

      if (myRequestId !== requestId) return;

      const interactions = store.getters["getInteractionsChannel"];

      ticketInteractionsChannels.value = [
        ...interactions.messages.filter((el) => el.channel?.name != "EMAIL"),
        ...interactions.voice,
        ...interactions.whatsapp_voice,
      ];
    } catch (error) {
      console.error(error);
    } finally {
      if (myRequestId === requestId) {
        currentPromise = null;
        currentTicketId = null;
      }
    }
  })();

  return currentPromise;
};

// La snapshot de interacciones queda stale cuando la llamada saliente
// termina: refetch en accepted/ended para que contact_date (contestada)
// llegue al header. Debounce corto porque el webhook del backend
// persiste el update de contestada un instante después del evento local.
let refetchTimer = null;

const scheduleInteractionsRefetch = () => {
  if (refetchTimer) clearTimeout(refetchTimer);
  refetchTimer = setTimeout(() => {
    refetchTimer = null;
    fetchInteractionsChannels();
  }, 1200);
};

watch(
  [() => route.query.ticket, () => route.query.interaction],
  async () => {
    const currentTicketId = Number(route.query.ticket);

    console.log("last fetchTicket: ", { currentTicketIdPending, currentTicketId: currentTicketId, ticketId: ticket.value?.id });
    // if (ticket.value?.id !== currentTicketId) await fetchTicket();
    // Se valida con el currentTicketIdPending para evitar que se haga la petición varias veces si el usuario cambia de ticket rápidamente 
    // (asegura que si se hace varias veces el ultimo si cargue)
    if (currentTicketIdPending !== currentTicketId) await fetchTicket();

    await fetchInteractionsChannels();
  },
  { immediate: true }
);

const interaction = computed(() => {
  const selectedInteractionId = Number(route.query.interaction);

  const currentInteraction = ticketInteractionsChannels.value.find(
    (interaction) => interaction.id === selectedInteractionId
  );

  return currentInteraction;
});

const { vueSocket } = useSocket();

const user = computed(() => store.getters["getDataUser"]);

vueSocket.subscribe("refreshChannels", async (data) => {
  console.log('refresh channels api compositions main layout: ', data)

  if (!route.path.includes("workspace")) return;
  if (!store.getters.isSocketEventForCurrentUser(data)) return;

  store.commit("SET_REFRESH_CHANNELS_ASTERISK_ID", data.asterisk_id ?? null);

  await fetchInteractionsChannels();

  // EventBus.$emit(WorkspaceEvent.RESET_LIST);
  if (!data.interaction_id) return;
  if (!data.is_call && route.query.interaction && data.interaction_id !== route.query.interaction)
    return;


  if (data.is_call && data.section != 'assign-client') {
    jssip.setMeta("interaction", data.interaction_id);
    console.log("fetchActiveWorkspaceTickets refreshChannels");
    store.dispatch("fetchActiveWorkspaceTickets", { mode: ModeFetchWorkspace.New });
  }

  console.log(data.interaction_id, "interactionId 8");
  console.log("CRISTINATEST - refreshChannels: ", {
    // ticket: data.ticket_id ?? route.query.ticket,
    ticket: route.query.ticket,
    interaction: data.interaction_id,
    client: route.query.client,
  });
  router.push({
    path: "/workspace",
    query: {
      // ticket: data.ticket_id ?? route.query.ticket,
      ticket: route.query.ticket,
      interaction: data.interaction_id,
      client: route.query.client,
    },
  });
});

const workspaceDataSource = computed(() => {
  return {
    tags: workspaceDataSourceLocal.tags,
  };
});

watch(interaction, () => {
  store.commit("SET_LAST_OPENED_TICKET", {
    ticket: ticket.value,
    interaction: interaction.value,
    client: route.query.client,
  });
});

provide("ticketInteractionsChannels", ticketInteractionsChannels);
provide("selectedTicket", ticket);
provide("selectedChannel", interaction);
provide("fetchTicket", fetchTicket);

// HOOKS
onBeforeUnmount(() => {
  store.commit("SET_TICKETS_FILTERS", {});
  if (refetchTimer) clearTimeout(refetchTimer);
  EventBus.off(Event.Accepted, scheduleInteractionsRefetch);
  EventBus.off(Event.Ended, scheduleInteractionsRefetch);
});

const dataUserState = computed(() => {
  return store.getters["getDataUserState"];
});

onMounted(async () => {
  await loadInitialData();
  const customerData = await getDataLoggedUser();
  console.log("customerData.enable_enter", customerData);
  localStorage.setItem("messageSendOption", customerData.enable_enter);
  if (route.query.ticket) {
    const { voice, messages, whatsapp_voice } = await ticketService.getInteractionsChannels(route.query.ticket);
    if (messages.length > 0) {
      setTimeout(async () => {
        await safeRegisterEventManagement(EventManagement.Opened, {
          open_ticket: ticket.value.id,
          menu: 23,
          state: dataUserState.value,
          module: 'new-workspace-template',
        });
        store.dispatch("setEventMessaging", EventManagement.Opened);
      }, 3000);
    }
  }

  EventBus.on(Event.Accepted, scheduleInteractionsRefetch);
  EventBus.on(Event.Ended, scheduleInteractionsRefetch);
});

const loadInitialData = async () => {
  ticketsListLoading.value = true;
  
  console.log("fetchActiveWorkspaceTickets loadInitialData");
  await store.dispatch("fetchActiveWorkspaceTickets");
  const response = await fetchListTags();
  workspaceDataSourceLocal.tags = response;
  console.log("CARGA - Datos iniciales del workspace cargados.");
  ticketsListLoading.value = false;
};

provide("workspaceDataSource", workspaceDataSource);
</script>

<style lang="sass" scoped>
.workspace
  height: calc(100vh - #{$header-height})
  background-color: $gray-5
  display: grid
  grid-template-columns: repeat(24, 1fr)
  // grid-template-columns: minmax(4fr, 4.1fr) minmax(4fr, 4.6fr) minmax(0, 10fr)
  // gap: 1px

  &__panel
    &--bordered
      border-left: $border-medium
      border-right: $border-medium

.with-banner
  height: calc(100vh - #{$header-height} - 35px)

.column-1
  grid-column: span 5

.column-2
  grid-column: span 8

.column-3
  grid-column: span 11
</style>
